import {LayoutModule} from '@angular/cdk/layout';
import {OverlayModule} from '@angular/cdk/overlay';
import {registerLocaleData} from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import localeRu from '@angular/common/locales/ru';
import { ErrorHandler, Injector, NgModule, inject, provideAppInitializer } from '@angular/core';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {MatPaginatorIntl} from '@angular/material/paginator';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {RouterModule} from '@angular/router';
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import {AppCommonModule} from '@common-module/app-common.module';
import {provideAppCalendarLocale} from '@common-module/app-components/date-controls/app-calendar-locale.service';
import {GlobalErrorHandler} from '@common-module/interceptors/global-error-handler';
import {HttpErrorsInterceptor} from '@common-module/interceptors/http-errors.interceptor';
import {
  AppCommonEnvironmentModule,
  EnvironmentHelper
} from '@common-module/services/environment-helper';
import {MatPaginationIntlService} from '@common-module/services/mat-pagination-intl.service';
import {AppComponent} from './app.component';
import {AppRoutingModule} from './app.routing.module';
import {initTranslationService} from './shared/config/translation.config';
import {buildKeycloakInitOptions} from './shared/config/keycloak.config';
import {isPublicLocation} from './shared/guards/auth.guard';


import {LazyTranslateLoader} from "@common-module/translate-config/LazyTranslateLoader";
import {AuthUserService} from "@common-module/app-services/auth-user.service";
import {filter, first} from "rxjs/operators";
import {AppCustomizationService} from "@common-module/app-services/app-customization.service";
import {MaterialModule} from "@common-module/material.module";
import {PermissionsSecurityService} from "./shared/services/permissions-security.service";
import {KeycloakAngularModule, KeycloakBearerInterceptor, KeycloakEventType, KeycloakService} from "keycloak-angular";
import {firstValueFrom} from "rxjs";
import {AuthInterceptor} from "./shared/interceptors/auth.interceptor";
import {UserPortalAuthUserService} from "./shared/services/user-portal-auth-user.service";


registerLocaleData(localeRu);


import {isPublicView} from './shared/utils/view-mode.utils';
export {isPublicView};

export function isLoginView(): boolean {
  return window.location.href.toLowerCase().includes('/login');
}

export function initAppCustomization(service: AppCustomizationService) {
  return () => {
    service.init();
    return service.customization$.pipe(
      filter(customization => customization !== null),
      first()
    );
  }
}

// должен ждать когда пользователь будет залогинен и получен авторизованный пользователь и его ограничения
export function initAuthUserAndPermissions(
  authUserService: AuthUserService,
  permissionSecurityService: PermissionsSecurityService,
  keycloak: KeycloakService,
  env: EnvironmentHelper
) {
  return () => {
    return keycloak.init({
      config: {
        url: env.keycloakAuth.URL,
        realm: env.keycloakAuth.REALM,
        clientId: env.keycloakAuth.CLIENT_ID,
      },
      initOptions: buildKeycloakInitOptions(isPublicLocation()),
      enableBearerInterceptor: true,
      bearerPrefix: 'Bearer',
    })
      .then(async (authenticated) => {
        // authenticated? залогинен ли пользователь
        if (!authenticated || isPublicView()) {
          return true;
        }

        permissionSecurityService.init();
        authUserService.init();

        await firstValueFrom(
          authUserService.currentUser$.pipe(
            filter(user => user !== null),
            first()
          )
        );

        return true;
      });
  };
}

@NgModule({ declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent],
    exports: [], imports: [KeycloakAngularModule,
        AppCommonEnvironmentModule.forRoot({
          portal: 'user',
          keycloakClientId: 'frontend-monitoring',
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: LazyTranslateLoader
            },
        }),
        MaterialModule,
        BrowserModule,
        AppCommonModule,
        AppCommonEnvironmentModule,
        OverlayModule,
        BrowserAnimationsModule,
        RouterModule,
        AppRoutingModule,
        LayoutModule], providers: [
        provideAppCalendarLocale(),
        { provide: AuthUserService, useClass: UserPortalAuthUserService },
        KeycloakService,
        provideAppInitializer(() => {
        const initializerFn = (initTranslationService)(inject(TranslateService), inject(Injector));
        return initializerFn();
      }),
        provideAppInitializer(() => {
        const initializerFn = (initAuthUserAndPermissions)(inject(AuthUserService), inject(PermissionsSecurityService), inject(KeycloakService), inject(EnvironmentHelper));
        return initializerFn();
      }),
        provideAppInitializer(() => {
        const initializerFn = (initAppCustomization)(inject(AppCustomizationService));
        return initializerFn();
      }),
        // {
        //   provide: APP_INITIALIZER,
        //   useFactory: initUnitGroupsService,
        //   deps: [UnitGroupsService],
        //   multi: true
        // },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: KeycloakBearerInterceptor,
            multi: true,
        },
        { provide: HTTP_INTERCEPTORS, useClass: HttpErrorsInterceptor, multi: true },
        MatBottomSheet,
        { provide: MatPaginatorIntl, useClass: MatPaginationIntlService },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
    ] })

export class AppModule {
}
